import { useEffect, useRef, useState } from 'react';
import { sendVisitPdf } from '../services/api';
import { getWorkOrderData } from '../services/api';
import { generateWorkOrderPDF } from '../utils/pdfGenerator';

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitId: number;
  patientEmail?: string;
  patientName: string;
}

export const SendEmailModal = ({
  isOpen,
  onClose,
  visitId,
  patientEmail,
  patientName,
}: SendEmailModalProps) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Prefill patient email on mount
  useEffect(() => {
    if (isOpen && patientEmail) {
      setEmails([patientEmail]);
    }
  }, [isOpen, patientEmail]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmails([]);
      setInputValue('');
      setError(null);
      setIsSending(false);
    }
  }, [isOpen]);

  const handleAddEmail = () => {
    const email = inputValue.trim();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingresa un correo válido');
      return;
    }

    if (emails.includes(email)) {
      setError('Este correo ya fue agregado');
      return;
    }

    setEmails([...emails, email]);
    setInputValue('');
    setError(null);
    inputRef.current?.focus();
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((e) => e !== emailToRemove));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      handleAddEmail();
    }
  };

  const handleSend = async () => {
    if (emails.length === 0) {
      setError('Por favor agrega al menos un correo electrónico');
      return;
    }

    try {
      setIsSending(true);
      setError(null);

      // Generate PDF as Blob
      const workOrderResponse = await getWorkOrderData(visitId);
      if (!workOrderResponse.success || !workOrderResponse.data) {
        throw new Error(
          workOrderResponse.error || 'No se pudo obtener los datos de la orden de trabajo'
        );
      }

      const pdfBlob = generateWorkOrderPDF(workOrderResponse.data, true) as Blob;

      // Send PDF via email
      await sendVisitPdf(visitId, pdfBlob, emails);

      // Close modal on success
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al enviar el correo';
      setError(errorMessage);
      console.error('Error sending email:', err);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-6">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl shadow-black/20">
        {/* Header */}
        <div className="px-4 sm:px-8 pt-8 pb-4">
          <div className="flex items-center gap-3 mb-2 text-blue-600">
            <span className="material-symbols-outlined text-3xl">mail</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">
            Enviar Registro de Visita
          </h2>
          <p className="text-slate-500 mt-2 text-base leading-relaxed">
            Ingresa los correos electrónicos para enviar el resumen de la visita de <b>{patientName}</b>.
          </p>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-8 py-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="email-input" className="text-sm font-semibold text-slate-700 ml-1">
              Correos Electrónicos
            </label>
            <div className="min-h-[3.5rem] p-2 bg-slate-50 border border-slate-200 rounded-xl flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-transparent transition-all">
              {emails.map((email) => (
                <div
                  key={email}
                  className="flex items-center gap-1.5 bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg border border-blue-300"
                >
                  <span className="text-sm font-semibold">{email}</span>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="flex items-center justify-center hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                    type="button"
                  >
                    <span className="material-symbols-outlined text-[18px]">close</span>
                  </button>
                </div>
              ))}
              <input
                ref={inputRef}
                id="email-input"
                type="email"
                placeholder="Añadir correo..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                className="flex-1 min-w-[120px] bg-transparent border-none focus:ring-0 p-1 text-slate-900 text-base outline-none"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-8 pb-8 pt-4 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            disabled={isSending}
            className="flex-1 order-2 sm:order-1 h-14 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 transition-colors disabled:opacity-50"
            type="button"
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || emails.length === 0}
            className="flex-1 order-1 sm:order-2 h-14 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
            type="button"
          >
            <span>{isSending ? 'Enviando Ticket...' : 'Enviar Ticket'}</span>
            {!isSending && <span className="material-symbols-outlined text-xl">send</span>}
          </button>
        </div>
      </div>
    </div>
  );
};
